/** Help-panel shortcut tiers and platform key labels (lint split from keyboardShortcuts). */
import { APP_KEYBOARD_SHORTCUTS } from '@/constants/keyboardShortcuts';

/**
 * Help panel: core shortcuts (always visible). Order matches the polish ticket.
 * @type {readonly string[]}
 */
export const HELP_CORE_SHORTCUT_IDS = Object.freeze([
  'quickAdd',
  'goBar',
  'pageNav',
  'listSelect',
  'listComplete',
  'listEdit',
  'listOpen',
  'listAddSubtask',
  'listFocus',
  'listDelete',
  'listReorder',
]);

/**
 * Help panel: extra shortcuts (collapsed by default). Order matches the polish ticket.
 * @type {readonly string[]}
 */
export const HELP_MORE_SHORTCUT_IDS = Object.freeze([
  'listAddAbove',
  'listAddBelow',
  'listIndent',
  'listOutdent',
  'listFollowUp',
  'listClearSelection',
  'inputSubmit',
  'inputCancel',
  'goBarNav',
  'calendarTaskNav',
  'organizeBulkEdit',
  'mouseMultiSelect',
  'dashboardBarNav',
  'filterBarNav',
]);

/** @param {readonly string[]} ids */
function shortcutsByIds(ids) {
  const byId = new Map(APP_KEYBOARD_SHORTCUTS.map((s) => [s.id, s]));
  return ids.map((id) => byId.get(id)).filter(Boolean);
}

/** @param {'core' | 'more'} tier */
export function getShortcutsForHelpTier(tier) {
  return shortcutsByIds(
    tier === 'more' ? HELP_MORE_SHORTCUT_IDS : HELP_CORE_SHORTCUT_IDS,
  );
}

/**
 * Apple platform detection for modifier key labels (safe non-Apple fallback).
 * @param {Pick<Navigator, 'platform' | 'userAgent'> & { userAgentData?: { platform?: string } } | undefined} [nav]
 */
export function isApplePlatform(
  nav = typeof navigator !== 'undefined' ? navigator : undefined,
) {
  if (!nav) return false;
  const uaDataPlatform = nav.userAgentData?.platform ?? '';
  if (uaDataPlatform && /mac|iphone|ipad|ipod/i.test(uaDataPlatform)) return true;
  if (uaDataPlatform) return false;
  const platform = nav.platform ?? '';
  if (/mac|iphone|ipad|ipod/i.test(platform)) return true;
  return /Mac|iPhone|iPad|iPod/.test(nav.userAgent ?? '');
}

/** i18n key for the GoBar shortcut label on the current platform. */
export function getGoBarKeysI18nKey(nav) {
  return isApplePlatform(nav)
    ? 'help.shortcuts.keys.goBarMac'
    : 'help.shortcuts.keys.goBar';
}
